import { API_BASE_URL } from '@/config/api';

export const CRM_PRIVACY_EVENT = 'umasaura:privacy-choice';
const PREFERENCE = 'umasaura_analytics_preference_v1';
const SESSION = 'umasaura_crm_session_v1';
type EventType = 'session.started' | 'page.viewed' | 'product.viewed' | 'search.performed' | 'wishlist.item_added' | 'wishlist.item_removed' | 'checkout.viewed';
type EventData = { route?: string; productId?: string; queryLength?: number; resultCount?: number };
type Activity = { eventId: string; eventType: EventType; occurredAt: number; data: EventData };
let queue: Activity[] = [];
let identity: string | null = null;
let anonymousId: string | null = null;
let sending = false;
let preferenceOverride: 'analytics' | 'essential' | null = null;

export function setCrmPrivacyChoice(value: 'analytics' | 'essential'): void {
  preferenceOverride = value;
  if (value === 'essential') clearCrmActivity();
}

export function crmAnalyticsAllowed(): boolean {
  try {
    return (preferenceOverride || localStorage.getItem(PREFERENCE)) === 'analytics' && localStorage.getItem('userRole') !== 'admin';
  } catch { return false; }
}
function currentIdentity(): string {
  return localStorage.getItem('authToken') ? localStorage.getItem('userId') || 'anonymous' : 'anonymous';
}
function session(): string | null {
  if (!crmAnalyticsAllowed()) { clearCrmActivity(); return null; }
  try {
    const next = currentIdentity();
    if (identity !== next) {
      queue = []; anonymousId = null; identity = next;
      const saved: { identity?: string; id?: string } = JSON.parse(sessionStorage.getItem(SESSION) || '{}');
      anonymousId = saved.identity === next && saved.id ? saved.id : crypto.randomUUID();
      sessionStorage.setItem(SESSION, JSON.stringify({ identity: next, id: anonymousId }));
    }
    return anonymousId;
  } catch { return null; }
}
export function clearCrmActivity(): void {
  queue = []; anonymousId = null; identity = null;
  try { sessionStorage.removeItem(SESSION); } catch { /* Shopping works without browser storage. */ }
}
export function trackCrmEvent(eventType: EventType, data: EventData = {}): void {
  if (typeof window === 'undefined' || document.visibilityState === 'hidden' || !session()) return;
  if (queue.length >= 40) queue.shift();
  queue.push({ eventId: crypto.randomUUID(), eventType, occurredAt: Date.now(), data });
}
export async function flushCrmActivity(): Promise<void> {
  if (sending || !queue.length) return;
  const before = identity;
  if (!session() || before !== identity || !queue.length) return;
  const batch = queue.splice(0, 10);
  const visitor = anonymousId;
  sending = true;
  try {
    const token = localStorage.getItem('authToken');
    const response = await fetch(`${API_BASE_URL}/api/crm/activity`, {
      method: 'POST', credentials: 'omit', keepalive: true,
      headers: { 'Content-Type': 'application/json', ...(token ? { token } : {}) },
      body: JSON.stringify({ analyticsAllowed: true, anonymousId: visitor, events: batch }),
    });
    if (response.status >= 500 && crmAnalyticsAllowed() && identity === before)
      queue = [...batch, ...queue].slice(0, 40).filter(item => Date.now() - item.occurredAt < 600000);
  } catch {
    if (crmAnalyticsAllowed() && identity === before)
      queue = [...batch, ...queue].slice(0, 40).filter(item => Date.now() - item.occurredAt < 600000);
  } finally { sending = false; }
}

/** Only coarse route labels are recorded. Never send query strings or customer/order URLs. */
export function crmRoute(path: string): string {
  if (path.startsWith('/admin') || path.startsWith('/cart-reminders/')) return '';
  if (path.startsWith('/product/')) return '/product';
  if (path.startsWith('/category/')) return '/category';
  if (path.startsWith('/blog/')) return '/blog';
  if (path.startsWith('/order/')) return '/order';
  if (path.startsWith('/campaigns/')) return '/campaign';
  if (['/privacy-policy', '/terms-conditions', '/shipping-policy', '/cancellation-refunds'].includes(path)) return '/policies';
  return ['/', '/blog', '/cart', '/checkout', '/wishlist', '/auth', '/profile', '/orders', '/order-success', '/gemfinder', '/contact-us'].includes(path) ? path : '/other';
}
